import type {
  TBulkActionErrorData,
  TBulkActionUpdateData,
  TRevokePermissionsUpdateData,
} from "utils/websockets/types";

import constate from "constate";
import { EventEmitter } from "events";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useLiveUpdate } from "hooks/useLiveUpdate";
import { useCallback, useRef } from "react";
import { WebSocketEvents, WebSocketRooms } from "utils/websockets/types";

const useAdminLiveUpdates = () => {
  const { user } = useAuthenticatedUser();
  const eventEmitterRef = useRef(new EventEmitter());

  const onBulkActionsUpdate = useCallback((data: null | TBulkActionErrorData | TBulkActionUpdateData) => {
    eventEmitterRef.current.emit(WebSocketEvents.BULK_EDIT_STATUS_UPDATE, data);
  }, []);

  const unsubscribeFromBulkActionsUpdate = useCallback(
    (callback: (data: null | TBulkActionErrorData | TBulkActionUpdateData) => void) => {
      eventEmitterRef.current.off(WebSocketEvents.BULK_EDIT_STATUS_UPDATE, callback);
    },
    [],
  );

  const subscribeToBulkActionsUpdate = useCallback(
    (callback: (data: null | TBulkActionErrorData | TBulkActionUpdateData) => void) => {
      eventEmitterRef.current.on(WebSocketEvents.BULK_EDIT_STATUS_UPDATE, callback);
    },
    [],
  );

  useLiveUpdate<null | TBulkActionErrorData | TBulkActionUpdateData>({
    eventName: WebSocketEvents.BULK_EDIT_STATUS_UPDATE,
    handleEvent: onBulkActionsUpdate,
    roomName: WebSocketRooms.COMPANY,
    shouldJoinRoom: user?.isAdmin ?? false,
  });

  const onRevokePermissionsUpdate = useCallback((data: null | TRevokePermissionsUpdateData) => {
    eventEmitterRef.current.emit(WebSocketEvents.REVOKE_PERMISSIONS_STATUS_UPDATE, data);
  }, []);

  const unsubscribeFromRevokePermissionsUpdate = useCallback(
    (callback: (data: null | TRevokePermissionsUpdateData) => void) => {
      eventEmitterRef.current.off(WebSocketEvents.REVOKE_PERMISSIONS_STATUS_UPDATE, callback);
    },
    [],
  );

  const subscribeToRevokePermissionsUpdate = useCallback(
    (callback: (data: null | TRevokePermissionsUpdateData) => void) => {
      eventEmitterRef.current.on(WebSocketEvents.REVOKE_PERMISSIONS_STATUS_UPDATE, callback);
    },
    [],
  );

  useLiveUpdate<null | TRevokePermissionsUpdateData>({
    eventName: WebSocketEvents.REVOKE_PERMISSIONS_STATUS_UPDATE,
    handleEvent: onRevokePermissionsUpdate,
    roomName: WebSocketRooms.COMPANY,
    shouldJoinRoom: user?.isAdmin ?? false,
  });

  return {
    subscribeToBulkActionsUpdate,
    subscribeToRevokePermissionsUpdate,
    unsubscribeFromBulkActionsUpdate,
    unsubscribeFromRevokePermissionsUpdate,
  };
};

export const [AdminLiveUpdatesProvider, useAdminLiveUpdatesContext] = constate(useAdminLiveUpdates);
